/**
 * AccountantRow: single table row with Option B action style.
 *
 * Primary action is a teal text link (Resend invite / View access).
 * Secondary action lives in a kebab (vertical ellipsis) that opens a
 * dropdown via createPortal(document.body) so it is never clipped by
 * the table's rounded corners or any ancestor's overflow:hidden.
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";
import type { MyAccountant } from "../myAccountantTypes";
import { formatDateShort, getInitials, getStatusLabel } from "../myAccountantUtils";
import { PALETTE } from "../constants";

interface AccountantRowProps {
  accountant: MyAccountant;
  onResend: (id: string) => void;
  onDelete: (id: string) => void;
  onViewAccess: (accountant: MyAccountant) => void;
}

export function AccountantRow({
  accountant,
  onResend,
  onDelete,
  onViewAccess,
}: AccountantRowProps) {
  const uid = useId().replace(/:/g, "");
  const styleTag = useAccountantRowStyles(uid, accountant.status);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const kebabRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const displayName = accountant.name?.trim() || accountant.email;
  const initials = getInitials(accountant.name, accountant.email);
  const statusLabel = getStatusLabel(accountant.status) || "Unknown";
  const addedDate = accountant.acceptedAt
    ? formatDateShort(accountant.acceptedAt)
    : formatDateShort(accountant.invitedAt);

  const primaryActionLabel =
    accountant.status === "active" ? "View access" : "Resend invite";

  const handlePrimary = useCallback(() => {
    if (accountant.status === "active") {
      onViewAccess(accountant);
    } else {
      onResend(accountant.id);
    }
  }, [accountant, onResend, onViewAccess]);

  const handleDelete = useCallback(() => {
    onDelete(accountant.id);
    setMenuOpen(false);
  }, [accountant.id, onDelete]);

  const toggleMenu = useCallback(() => {
    if (!menuOpen && kebabRef.current) {
      const rect = kebabRef.current.getBoundingClientRect();
      const menuWidth = 160;
      setMenuPos({
        top: rect.bottom + window.scrollY + 6,
        left: rect.right + window.scrollX - menuWidth,
      });
    }
    setMenuOpen((o) => !o);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        kebabRef.current &&
        !kebabRef.current.contains(target)
      ) {
        setMenuOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    const onScroll = () => setMenuOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [menuOpen]);

  return (
    <>
      {styleTag}
      <div className={`ar-${uid}-row`}>
        <div className={`ar-${uid}-name-cell`}>
          <div className={`ar-${uid}-ava`} aria-hidden="true">
            {initials}
          </div>
          <span className={`ar-${uid}-name`}>{displayName}</span>
        </div>
        <div className={`ar-${uid}-email`}>{accountant.email}</div>
        <div className={`ar-${uid}-firm`}>
          {accountant.firmName || (
            <span className={`ar-${uid}-empty`}>&nbsp;</span>
          )}
        </div>
        <div className={`ar-${uid}-status`}>{statusLabel}</div>
        <div className={`ar-${uid}-date`}>{addedDate}</div>
        <div className={`ar-${uid}-action`}>
          <button
            type="button"
            className={`ar-${uid}-primary-link`}
            onClick={handlePrimary}
          >
            {primaryActionLabel}
          </button>
          <button
            type="button"
            className={`ar-${uid}-kebab`}
            onClick={toggleMenu}
            ref={kebabRef}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="More actions"
          >
            <MoreVertical size={16} strokeWidth={2} />
          </button>
        </div>
      </div>

      {menuOpen &&
        createPortal(
          <div
            ref={menuRef}
            className={`ar-${uid}-menu`}
            role="menu"
            style={{
              position: "absolute",
              top: menuPos.top,
              left: menuPos.left,
            }}
          >
            <button
              type="button"
              className={`ar-${uid}-menu-item ar-${uid}-menu-delete`}
              onClick={handleDelete}
              role="menuitem"
            >
              Delete
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}

function useAccountantRowStyles(uid: string, status: MyAccountant["status"]) {
  const statusColor =
    status === "active"
      ? PALETTE.teal
      : status === "expired"
        ? PALETTE.red
        : PALETTE.amber;

  const css = `
    .ar-${uid}-row {
      display: grid;
      grid-template-columns: 1.3fr 2fr 1.4fr 1fr 1fr auto;
      gap: 18px;
      padding: 14px 20px;
      align-items: center;
      font-size: 13px;
      color: ${PALETTE.ink};
      border-top: 1px solid ${PALETTE.line};
      transition: background 140ms;
    }
    .ar-${uid}-row:hover { background: ${PALETTE.page}; }

    .ar-${uid}-name-cell {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
    }
    .ar-${uid}-ava {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      background: ${PALETTE.ink};
      color: #fff;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 10.5px;
      font-weight: 600;
      flex-shrink: 0;
    }
    .ar-${uid}-name {
      font-weight: 600;
      color: ${PALETTE.ink};
      font-size: 13px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .ar-${uid}-email, .ar-${uid}-firm, .ar-${uid}-date {
      color: ${PALETTE.ink2};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .ar-${uid}-empty { color: ${PALETTE.ink4}; }
    .ar-${uid}-status {
      color: ${statusColor};
      font-weight: 600;
      font-size: 12.5px;
    }

    .ar-${uid}-action {
      display: flex;
      gap: 10px;
      align-items: center;
      justify-content: flex-end;
      min-width: 160px;
    }
    .ar-${uid}-primary-link {
      background: transparent;
      border: 0;
      color: ${PALETTE.teal};
      padding: 6px 2px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
    }
    .ar-${uid}-primary-link:hover {
      text-decoration: underline;
      text-underline-offset: 3px;
    }
    .ar-${uid}-kebab {
      background: transparent;
      border: 1px solid ${PALETTE.line};
      color: ${PALETTE.ink2};
      padding: 5px 7px;
      border-radius: 7px;
      cursor: pointer;
      font-family: inherit;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: background 140ms, border-color 140ms;
    }
    .ar-${uid}-kebab:hover {
      background: ${PALETTE.lineSoft};
      border-color: ${PALETTE.ink4};
      color: ${PALETTE.ink};
    }

    .ar-${uid}-menu {
      background: #ffffff;
      border: 1px solid ${PALETTE.line};
      border-radius: 8px;
      box-shadow: 0 10px 24px -4px rgba(14, 26, 26, 0.22);
      min-width: 160px;
      z-index: 1000;
      overflow: hidden;
      animation: ar-${uid}-pop 140ms ease-out;
    }
    @keyframes ar-${uid}-pop {
      from { opacity: 0; transform: translateY(-4px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .ar-${uid}-menu-item {
      background: transparent;
      border: 0;
      padding: 10px 14px;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      font-family: inherit;
      display: block;
      width: 100%;
      text-align: left;
      color: ${PALETTE.ink};
      transition: background 120ms;
    }
    .ar-${uid}-menu-item:hover { background: ${PALETTE.page}; }
    .ar-${uid}-menu-delete { color: ${PALETTE.red}; }
    .ar-${uid}-menu-delete:hover { background: ${PALETTE.redBg}; }

    @media (max-width: 720px) {
      .ar-${uid}-row {
        grid-template-columns: 1fr;
        gap: 4px;
        padding: 14px 16px;
      }
      .ar-${uid}-name-cell { margin-bottom: 2px; }
      .ar-${uid}-email, .ar-${uid}-firm, .ar-${uid}-date { font-size: 12px; }
      .ar-${uid}-action { justify-content: flex-end; margin-top: 8px; min-width: 100%; }
    }
  `;
  return <style>{css}</style>;
}